import { useEffect, useState } from "react";

import { VideoJS } from "@components/VideoJS";

import { LoadingHand } from "./LoadingHand";
import { MdPlayArrow, MdVolumeOff, MdVolumeUp } from "react-icons/md";
import { Slider } from "@components/ui/slider";
import { cn } from "@lib/utils";
import type Player from "video.js/dist/types/player";
import router from "next/router";

type VideoSlideProps = {
  videoSrc: string;
  videoPoster: string;
  playVideo: boolean;
  onVideoEnded: () => void;

  onVideoPlay: (player: any) => void;

  onSlideStart?: () => void;
  onSlideEnd?: () => void;

  isActive: boolean;
  isSingleActivity: boolean;
};

export const VideoSlide = ({
  videoSrc,
  videoPoster,
  playVideo,
  onVideoEnded,
  onVideoPlay,
  onSlideStart,
  onSlideEnd,
  isActive,
  isSingleActivity,
}: VideoSlideProps) => {
  const [player, setPlayer] = useState<Player | null>(null);

  const [isVideoLoading, setIsVideoLoading] = useState(true);
  const [isMuted, setIsMuted] = useState(false);
  const [isPaused, setIsPaused] = useState(false);

  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);

  useEffect(() => {
    playVideo && !isPaused && player?.play();
  });

  useEffect(() => {
    player?.muted(isMuted);
  }, [isMuted]);

  useEffect(() => {
    isPaused && player?.pause();
  }, [isPaused]);


  return (
    <div
      className="relative w-full h-[100dvh]"
      onClick={() => setIsPaused(!isPaused)}
    >
      <VideoJS
        options={{
          autoplay: false,
          controls: false,
          loop: false,
          playsinline: true,
          height:
            (window.innerWidth > 768
              ? window.innerHeight * 0.95
              : window.innerHeight) - 30, // 30 is the booking bar height
          width: window.innerWidth < 512 ? window.innerWidth : 512,
          preload: isActive ? "auto" : "none",
        }}
        play={playVideo}
        src={videoSrc}
        poster={videoPoster}
        onEnded={onVideoEnded}
        onReady={(playerRef) => {
          setPlayer(playerRef);
          setDuration(playerRef?.duration());
        }}
        onPlay={(playerRef) => {
          onVideoPlay(playerRef);
          setDuration(playerRef?.duration());
          setIsPaused(false);
        }}
        onPlaying={() => setIsVideoLoading(false)}
        onWaiting={() => setIsVideoLoading(true)}
        onLoaded={(playerRef) => {
          playVideo && playerRef.play();
          setDuration(playerRef?.duration());
          setIsVideoLoading(false);
        }}
        onLoadedMetadata={() => setIsVideoLoading(false)}
        onTimeUpdate={(p) => setCurrentTime(p?.currentTime())}
      />

      <div
        className={cn( 
          "absolute z-50 w-full h-[100dvh] flex flex-col justify-end items-end gap-2 px-4 py-2",
          isSingleActivity
            ? "bottom-[170px] md:bottom-[220px]"
            : "bottom-[122px] md:bottom-[172px]"
        )}
      >
        <button
          onClick={(e) => {
            e.stopPropagation();
            setIsMuted(!isMuted);
          }}
          className="flex items-center justify-center bg-gray-700 text-gray-100 text-lg rounded-full w-8 h-8"
        >
          {isMuted ? <MdVolumeOff /> : <MdVolumeUp />}
        </button>
        <Slider
          onClick={(e) => e.stopPropagation()}
          className="z-50 w-full transition-all duration-300"
          defaultValue={[currentTime]}
          value={[currentTime]}
          max={duration}
          step={0.5}
          onValueChange={([value]) => player?.currentTime(value)}
          onTouchStart={onSlideStart}
          onTouchEnd={onSlideEnd}
          onDragStart={onSlideStart}
          onDragEnd={onSlideEnd}
          onPointerDown={onSlideStart}
          onPointerUp={onSlideEnd}
        />
      </div>

      {isPaused && (
        <div className="absolute top-1/2 md:top-[43%] left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-[9999] bg-gray-700 text-gray-100 text-lg rounded-full w-12 h-12">
          <MdPlayArrow className="w-full h-full" />
        </div>
      )}

      {isVideoLoading && <LoadingHand />}
    </div>
  );
};
